import isOnline from "is-online";
import { API_URL, API_DEBUG } from "../config";
import { clearCmsStorage } from "../helpers/storageUtils";

/**
 * Gets or fetches a CSRF token
 *
 * @param {string} csrfURL - URL to fetch CSRF token from
 * @returns {Promise<string|boolean>} - The CSRF token or false if it couldn't be fetched
 */
const getOrFetchCSRFToken = async (csrfURL, forceFetch = false) => {
    const key = csrfURL
        .replace(/^\//, "")
        .replace(/\/$/, "")
        .replace(/\//g, "_");
    const tokenKey = `${key}_token`;

    if (forceFetch) {
        localStorage.removeItem(tokenKey);
    }

    // Then check localStorage
    const storedToken = localStorage.getItem(tokenKey);
    if (storedToken) {
        return storedToken;
    }

    // If not found, fetch a new token
    try {
        const headers = {
            Accept: "application/json",
            "Content-Type": "application/json",
        };

        const settings = {
            method: "GET",
            headers,
            credentials: "include",
        };

        const response = await fetch(API_URL + csrfURL, settings);
        const data = await response.json();

        if (data?.token) {
            localStorage.setItem(tokenKey, data.token);
            return data.token;
        }

        return false;
    } catch (error) {
        console.error("Error fetching CSRF token:", error);
        return false;
    }
};

/**
 * Checks if the user is connected
 *
 * @returns {Promise<boolean>} - Whether the user is connected
 */
const checkIsConnected = async (dependencies = {}) => {
    let url = "/login";
    let method = "GET";
    const data = await apiRequest({
        url,
        method,
        dependencies: dependencies,
    });

    if (data && data.status === "ok" && data.loggedIn) return true;
    return false;
};

/**
 * Updates the session timer
 *
 * @param {boolean} firstRequest - Whether this is the first request
 * @param {Function} safeNavigate - Function to navigate to a route
 */
const updateTimerSession = (firstRequest, safeNavigate) => {
    const rememberMe =
        localStorage.getItem("_remember_me") &&
        localStorage.getItem("_remember_me") !== "true";

    // 5min session when _remember_me is false
    const timerSession =
        !isNaN(localStorage.getItem("session")) &&
        new Date().getTime() - parseInt(localStorage.getItem("session")) <
            300000;

    if (rememberMe && (firstRequest || timerSession)) {
        localStorage.setItem("session", JSON.stringify(new Date().getTime()));
    } else if (rememberMe && !firstRequest && !timerSession) {
        clearCmsStorage();
        safeNavigate("/login");
    }
};

/**
 * Makes an asynchronous request to the specified URL using the given method and optional body.
 *
 * @param {object} config - Configuration object containing the following properties:
 * @param {string} config.url - The URL to send the request to.
 * @param {string} config.method - The HTTP method to use for the request (e.g., "GET", "POST").
 * @param {string|object} [config.body=""] - The request body (optional).
 * @param {boolean} [config.isUploadForm=false] - Indicates whether the request is an upload form (optional).
 * @param {string} [config.csrfURL=""] - The URL to fetch CSRF token from (optional).
 * @param {boolean} [config.firstRequest=false] - Whether this is the first request (optional).
 * @param {object} [config.errorHandler={ isHandlingError: () => false, handleError: () => {} }] - The error handler object (optional).
 * @param {object} config.dependencies - Object containing dependencies for request handling:
 * @param {Function} config.dependencies.navigate - Function to navigate to a new route
 * @param {Function} config.dependencies.addToast - Function to display toast notifications
 * @param {Function} config.dependencies.t - Translation function
 * @param {object} config.dependencies.i18n - i18n instance
 * @returns {Promise<object|null>} - A promise that resolves to the response data if successful, or null if an error occurred.
 */
export const apiRequest = async ({
    url,
    method = "GET",
    body = "",
    isUploadForm = false,
    csrfURL = "",
    firstRequest = false,
    errorHandler = { isHandlingError: () => false, handleError: () => {} },
    dependencies,
}) => {
    const { navigate, addToast, t, i18n } = dependencies;

    const safeNavigate = (path) => {
        setTimeout(() => {
            navigate(path);
        }, 0);
    };

    // Handle CSRF token if needed
    if (
        csrfURL &&
        csrfURL !== "" &&
        (method === "POST" || method === "PUT" || method === "DELETE")
    ) {
        const csrfToken = await getOrFetchCSRFToken(csrfURL);

        // Add CSRF token to body
        if (csrfToken) {
            if (body instanceof FormData) {
                body.append("_csrf_token", csrfToken);
            } else if (typeof body === "string") {
                try {
                    const bodyObj = body ? JSON.parse(body) : {};
                    bodyObj._csrf_token = csrfToken;
                    body = JSON.stringify(bodyObj);
                } catch {
                    body = JSON.stringify({ _csrf_token: csrfToken });
                }
            } else if (typeof body === "object" && body !== null) {
                body._csrf_token = csrfToken;
                body = JSON.stringify(body);
            } else {
                body = JSON.stringify({ _csrf_token: csrfToken });
            }
        }
    }

    // Prepare headers
    let headers = {};
    if (!isUploadForm) {
        headers = {
            Accept: "application/json",
            "Content-Type": "application/json",
        };
    }

    // Prepare request settings
    const settings = {
        method,
        headers,
        credentials: "include",
        ...(body && { body }),
    };

    let hasResponse = false;

    try {
        const response = await fetch(API_URL + url, settings);
        const data = await response.json();

        // Handle error response
        if (data.status === "error") {
            if (data.message === "Invalid CSRF") {
                const newCsrfToken = await getOrFetchCSRFToken(csrfURL, true);
                if (newCsrfToken) {
                    return apiRequest({
                        url,
                        method,
                        body,
                        isUploadForm,
                        csrfURL,
                        firstRequest,
                        errorHandler,
                        dependencies,
                    });
                }
            }

            if (errorHandler?.isHandlingError(data, response.status)) {
                return errorHandler?.handleError(data);
            } else {
                hasResponse = data ? true : false;
                API_DEBUG && console.log(data);

                const message = i18n.exists(data.message)
                    ? t(data.message)
                    : t("GENERIC_ERROR");
                addToast(message, { appearance: "danger" });
                console.error(data.message);

                // Handle authentication errors
                if (data.message === "Invalid Credentials") {
                    const loggedIn = await checkIsConnected(dependencies);
                    if (!loggedIn) {
                        clearCmsStorage();
                        addToast &&
                            addToast(t && t("disconnectedMsg"), {
                                appearance: "danger",
                            });
                        safeNavigate("/login");
                    }
                }

                if (data.message === "Disconnected") {
                    clearCmsStorage();
                    safeNavigate("/login");
                }

                return data;
            }
        }

        // Check response status
        if (!response.ok) throw Error(response.statusText);

        // Update session timer
        updateTimerSession(firstRequest, safeNavigate);

        // Debug logging
        API_DEBUG && console.log(data);

        // Record activity timestamp
        localStorage.setItem("_activity_timestamp", `${Date.now()}`);

        return data;
    } catch (e) {
        console.log("e", e);

        // Handle network errors
        if (
            hasResponse &&
            !(await checkIsConnected(dependencies)) &&
            localStorage.getItem("account")
        ) {
            addToast(t("disconnectedMsg"), {
                appearance: "danger",
            });
            clearCmsStorage();
            safeNavigate("/login");
        } else if (!hasResponse) {
            if (await isOnline({ timeout: 1000 })) {
                // TODO: msg API server is offline
            } else {
                addToast(t("offlineMsg"), {
                    appearance: "danger",
                });
            }
        } else {
            console.error(e.toString());
        }
        return null;
    }
};
